/**
 * Tarification SwiftDeliver — UNE seule règle, côté TypeScript.
 *
 * Miroir exact de la fonction SQL `public.tarif_abonnement()` (migration 0027).
 * Trois consommateurs, un seul calcul :
 *   • l'edge function `create-subscription-payment` (montant autoritatif) ;
 *   • le portail, écran Abonnement (total qui se recalcule à l'écran) ;
 *   • la supervision (MRR).
 * Les mêmes vecteurs de test tournent côté TypeScript (vitest) et côté SQL
 * (supabase/tests/gps/20_scenarios_tarifs.sql) : si les deux divergent, un test
 * casse. Ne JAMAIS recoder ce calcul ailleurs.
 *
 * Fichier SANS dépendance et sans API Deno ni DOM : il est importé tel quel par
 * Deno (edge function, extension `.ts`) et par Vite (portail, supervision).
 *
 * La règle (décision du 04/10/2026) :
 *   mensuel = prix du palier
 *           + véhicules supplémentaires × prix unitaire   (Starter et Pro)
 *           + boîtiers GPS actifs       × prix unitaire   (les trois paliers depuis la 0053 ; NULL = option inexistante)
 *   mensuel  → on règle `mensuel` chaque mois ;
 *   annuel   → on règle `mensuel × 10` pour 12 mois (« 2 mois offerts ») ;
 *   MRR      → le mensuel ; en annuel, le total annuel / 12, arrondi à l'unité.
 * Palier sans prix (Business, sur devis) : aucun total, jamais compté à 0.
 */

export type PalierTarif = 'starter' | 'pro' | 'business'
export type PeriodeFacturation = 'monthly' | 'annual'

export const PERIODES: readonly PeriodeFacturation[] = ['monthly', 'annual']

/** Mois payés pour 12 en annuel — valeur du catalogue en 0027, repli si la colonne n'est pas encore lisible. */
export const MOIS_PAYES_ANNUEL_DEFAUT = 10
/** Garde-fou d'interface et de paiement (la base n'impose que ≥ 0). */
export const MAX_VEHICULES_SUPP = 50

/** Prix unitaires applicables à UN abonnement : ceux du catalogue, ou ceux figés à la souscription. */
export interface PrixUnitaires {
  /** Palier, par mois. null = sur devis. */
  base: number | null
  /** Véhicule supplémentaire, par mois. null = option inexistante pour ce palier. */
  vehiculeSupp: number | null
  /** Boîtier GPS actif, par mois. null = option inexistante pour ce palier. */
  boitier: number | null
  /** Mois réglés pour 12 en annuel. */
  moisPayesAnnuel: number
}

/** Prix figés à la souscription (colonnes `locked_*` de `subscriptions`). null = non figé. */
export interface PrixFiges {
  base: number | null
  vehiculeSupp: number | null
  boitier: number | null
  moisPayesAnnuel: number | null
}

export interface Selection {
  vehiculesSupp: number
  boitiersActifs: number
  periode: PeriodeFacturation
}

export interface Tarif {
  /** Prix du palier seul, par mois. null = sur devis. */
  base: number | null
  /** Véhicules supplémentaires, par mois. */
  montantVehiculesSupp: number
  /** Boîtiers GPS actifs, par mois (0 si l'option n'existe pas pour ce palier). */
  montantBoitiers: number
  /** Total mensuel (formule mensuelle). null = sur devis. */
  mensuel: number | null
  /** 1 en mensuel ; `moisPayesAnnuel` en annuel. */
  moisFactures: number
  /** Montant à régler pour la période choisie. null = sur devis. */
  du: number | null
  /** Équivalent mensuel pour le MRR : mensuel, ou du / 12 arrondi en annuel. */
  mrr: number | null
  /** Ce que l'annuel fait économiser par rapport à 12 mensualités (0 en mensuel). */
  economieAnnuelle: number
}

export type CodeErreurTarif =
  | 'vehicules_supp_invalide'
  | 'vehicules_supp_non_proposes'
  | 'boitiers_invalide'
  | 'periode_invalide'
  | 'mois_annuel_invalide'

export class ErreurTarif extends Error {
  readonly code: CodeErreurTarif
  constructor(code: CodeErreurTarif) {
    super(code)
    this.name = 'ErreurTarif'
    this.code = code
  }
}

const entierPositif = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0

/**
 * Le calcul. Pur, déterministe, sans arrondi caché sauf le MRR annuel.
 * Lève `ErreurTarif` pour une saisie incohérente (le serveur répond 400,
 * l'interface ne l'atteint pas : elle borne ses champs).
 */
export function calculerTarif(prix: PrixUnitaires, sel: Selection): Tarif {
  if (!entierPositif(sel.vehiculesSupp)) throw new ErreurTarif('vehicules_supp_invalide')
  if (!entierPositif(sel.boitiersActifs)) throw new ErreurTarif('boitiers_invalide')
  if (!PERIODES.includes(sel.periode)) throw new ErreurTarif('periode_invalide')
  if (!Number.isInteger(prix.moisPayesAnnuel) || prix.moisPayesAnnuel < 1 || prix.moisPayesAnnuel > 12) {
    throw new ErreurTarif('mois_annuel_invalide')
  }
  // Acheter des véhicules supplémentaires sur un palier qui n'en vend pas est
  // une erreur ; des boîtiers déjà actifs sur un palier sans option ne sont
  // simplement pas facturés (c'est une donnée, pas un choix d'achat).
  if (sel.vehiculesSupp > 0 && prix.vehiculeSupp === null) throw new ErreurTarif('vehicules_supp_non_proposes')

  if (prix.base === null) {
    return {
      base: null, montantVehiculesSupp: 0, montantBoitiers: 0, mensuel: null,
      moisFactures: sel.periode === 'annual' ? prix.moisPayesAnnuel : 1,
      du: null, mrr: null, economieAnnuelle: 0,
    }
  }

  const montantVehiculesSupp = sel.vehiculesSupp * (prix.vehiculeSupp ?? 0)
  const montantBoitiers = prix.boitier === null ? 0 : sel.boitiersActifs * prix.boitier
  const mensuel = prix.base + montantVehiculesSupp + montantBoitiers
  const annuel = sel.periode === 'annual'
  const moisFactures = annuel ? prix.moisPayesAnnuel : 1
  const du = mensuel * moisFactures
  return {
    base: prix.base,
    montantVehiculesSupp,
    montantBoitiers,
    mensuel,
    moisFactures,
    du,
    // Math.round : demi vers le haut, comme `round()` de Postgres sur un numeric positif.
    mrr: annuel ? Math.round(du / 12) : mensuel,
    economieAnnuelle: annuel ? mensuel * 12 - du : 0,
  }
}

/**
 * Prix applicables à un abonnement : le prix FIGÉ à la souscription quand il
 * existe, sinon celui du catalogue, champ par champ (comme `coalesce` en SQL).
 *
 * Quand figer ? À la souscription, au changement de palier et au passage à
 * « actif » depuis essai ou résilié (déclencheur `figer_prix_abonnement`,
 * 0027). Un renouvellement du même palier garde le prix de départ : un client
 * existant ne subit pas rétroactivement une hausse du catalogue.
 */
export function prixApplicables(catalogue: PrixUnitaires, figes: PrixFiges | null): PrixUnitaires {
  if (!figes) return catalogue
  return {
    base: figes.base ?? catalogue.base,
    vehiculeSupp: figes.vehiculeSupp ?? catalogue.vehiculeSupp,
    boitier: figes.boitier ?? catalogue.boitier,
    moisPayesAnnuel: figes.moisPayesAnnuel ?? catalogue.moisPayesAnnuel,
  }
}

/**
 * Le prix figé s'applique-t-il à ce paiement ? Seulement pour un abonnement
 * déjà payant (actif ou impayé) ET pour le MÊME palier : changer de palier, ou
 * transformer un essai en abonnement, est une nouvelle souscription — au
 * catalogue du jour.
 */
export function prixFigesApplicables(
  abonnement: { palier: string; statut: string | null } | null,
  palierDemande: string,
): boolean {
  return (
    abonnement !== null &&
    abonnement.palier === palierDemande &&
    (abonnement.statut === 'active' || abonnement.statut === 'past_due')
  )
}

/** Date de fin de période après un paiement : +1 mois, ou +12 mois en annuel (on a payé 10 mois pour 12). */
export function finDePeriode(depuis: Date, periode: PeriodeFacturation): Date {
  const d = new Date(depuis.getTime())
  const jour = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() + (periode === 'annual' ? 12 : 1))
  const dernier = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  d.setDate(Math.min(jour, dernier))
  return d
}

export function estPeriode(v: unknown): v is PeriodeFacturation {
  return v === 'monthly' || v === 'annual'
}
